import { observer } from "mobx-react-lite"
import { useLocation, useNavigate } from "react-router-dom"

import type { ComplexTab } from "@/domains/navigation/components/complex-breadcrumb-dropdown"
import { Tabs, TabsList, TabsTrigger } from "@/domains/common/components/tabs"
import { cn } from "@/domains/library"

export interface ComplexTabsListProps {
  tabs: ComplexTab[]
  className?: string
}

/**
 * Reusable tabs component for complex navigation tabs.
 * Handles tab selection, navigation, icon rendering, and equal-width distribution.
 */
export const ComplexTabsList = observer(({ tabs, className }: ComplexTabsListProps) => {
  const location = useLocation()
  const navigate = useNavigate()

  // Find current tab
  const currentTab = tabs.find((tab) => location.pathname.endsWith(tab.slug)) ?? tabs[0]

  // Handle tab selection - replace the last part of the URL
  const handleTabSelect = (tabPath: string) => {
    const fullUrl = location.pathname + location.search + location.hash
    const newUrl = fullUrl.split("/").slice(0, -1).join("/") + tabPath
    navigate(newUrl)
  }

  // Calculate equal width for tabs
  // Tailwind doesn't support dynamic classes, so we map common tab counts to width classes
  const getTabWidthClass = (count: number): string => {
    switch (count) {
      case 2:
        return "w-1/2"
      case 3:
        return "w-1/3"
      case 4:
        return "w-1/4"
      case 5:
        return "w-1/5"
      case 6:
        return "w-1/6"
      default:
        return "w-full"
    }
  }

  const tabWidthClass = getTabWidthClass(tabs.length)

  return (
    <Tabs onValueChange={handleTabSelect} defaultValue={currentTab?.slug ?? tabs[0]?.slug} className={className}>
      <TabsList className="w-full">
        {tabs.map((tab) => (
          <TabsTrigger key={tab.id} value={tab.slug} className={cn(tabWidthClass)}>
            <div className="flex items-center gap-2">
              {tab.icon && <tab.icon className="-mr-0.5 text-t100" size={14} />}
              {tab.tabLabel ?? tab.label}
            </div>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
})
